import {
  allEnumValues,
  KioskDeviceType,
  KioskDeviceTypeHelper,
  KioskPaymentTerminalModel,
  KioskPaymentTerminalModelHelper,
  PrinterType,
  PrinterTypeHelper,
} from '@biteinc/enums';
import { CsvHelper } from '@biteinc/helpers';

export type KioskImportRow = {
  serialNumber: string;
  locationId: string;
  name?: string;
  deviceType: KioskDeviceType;
  printerType: PrinterType;
  /** stored as typed; "USB Printer" is the kiosk schema's own default */
  printerMacAddress?: string;
  paymentTerminalModel: KioskPaymentTerminalModel;
  paymentTerminalSerialNumber?: string;
  paymentTerminalIpAddress?: string;
  /** only sent when TRUE; the server rejects a peripheral the device cannot use */
  hasScanner?: true;
  hasHid?: true;
  hasMsr?: true;
  hasScale?: true;
  hasStormAudioNav?: true;
  notes?: string;
};

export const KioskImportHeaders = [
  'Serial Number',
  'Location ID',
  'Name',
  'Kiosk Type',
  'Printer',
  'Printer MAC Address',
  'Payment Terminal',
  'Payment Terminal Identifier',
  'Payment Terminal IP Address',
  'Scanner',
  'HID',
  'MSR',
  'Scale',
  'Storm Audio Nav',
  'Notes',
];

const RequiredHeaders = [
  'serial number',
  'location id',
  'kiosk type',
  'printer',
  'payment terminal',
];

const PeripheralFieldByHeader = {
  scanner: 'hasScanner',
  hid: 'hasHid',
  msr: 'hasMsr',
  scale: 'hasScale',
  'storm audio nav': 'hasStormAudioNav',
} as const;

const PeripheralLabelByHeader: Record<keyof typeof PeripheralFieldByHeader, string> = {
  scanner: 'Scanner',
  hid: 'HID',
  msr: 'MSR',
  scale: 'Scale',
  'storm audio nav': 'Storm Audio Nav',
};

const MongoIdRegex = /^[0-9a-f]{24}$/;

function buildValueByLabel<EnumType extends number>(
  tsEnum: object,
  name: (value: EnumType) => string,
): Map<string, EnumType> {
  const valueByLabel = new Map<string, EnumType>();
  allEnumValues<EnumType>(tsEnum).forEach((value) => {
    valueByLabel.set(name(value).toLowerCase(), value);
  });
  return valueByLabel;
}

const DeviceTypeByLabel = buildValueByLabel<KioskDeviceType>(
  KioskDeviceType,
  KioskDeviceTypeHelper.name,
);
const PrinterTypeByLabel = buildValueByLabel<PrinterType>(PrinterType, PrinterTypeHelper.name);
const PaymentTerminalModelByLabel = buildValueByLabel<KioskPaymentTerminalModel>(
  KioskPaymentTerminalModel,
  KioskPaymentTerminalModelHelper.name,
);

function parseRows(
  headerCells: string[],
  dataRows: string[][],
  errorMessages: string[],
): KioskImportRow[] {
  const headerRow = headerCells.map((header) => header.trim().toLowerCase());
  const missingHeaders = RequiredHeaders.filter((header) => !headerRow.includes(header));
  if (missingHeaders.length) {
    errorMessages.push(`The CSV file is missing required columns: ${missingHeaders.join(', ')}.`);
    return [];
  }

  const columnIndexByHeader = new Map<string, number>();
  headerRow.forEach((header, columnIndex) => {
    if (!columnIndexByHeader.has(header)) {
      columnIndexByHeader.set(header, columnIndex);
    }
  });

  const kioskRows: KioskImportRow[] = [];
  dataRows.forEach((csvRow, rowIndex) => {
    // Spreadsheet exports keep trailing blank rows as empty cells.
    if (csvRow.every((value) => !value.trim())) {
      return;
    }
    const rowNumber = rowIndex + 1;
    function cell(header: string): string {
      const columnIndex = columnIndexByHeader.get(header);
      return columnIndex !== undefined ? (csvRow[columnIndex] || '').trim() : '';
    }
    function enumCell<EnumType>(
      header: string,
      label: string,
      valueByLabel: Map<string, EnumType>,
    ): EnumType | undefined {
      const value = valueByLabel.get(cell(header).toLowerCase());
      if (value === undefined) {
        errorMessages.push(`Row ${rowNumber}: "${cell(header)}" is not a known ${label}.`);
      }
      return value;
    }

    const serialNumber = cell('serial number');
    if (!serialNumber) {
      errorMessages.push(`Row ${rowNumber}: A serial number is required.`);
    }

    const locationId = cell('location id').toLowerCase();
    if (!MongoIdRegex.test(locationId)) {
      errorMessages.push(`Row ${rowNumber}: "${cell('location id')}" is not a valid Location ID.`);
    }

    const deviceType = enumCell('kiosk type', 'Kiosk Type', DeviceTypeByLabel);
    const printerType = enumCell('printer', 'Printer', PrinterTypeByLabel);
    const paymentTerminalModel = enumCell(
      'payment terminal',
      'Payment Terminal',
      PaymentTerminalModelByLabel,
    );

    const peripheralFlags: Partial<
      Pick<KioskImportRow, 'hasScanner' | 'hasHid' | 'hasMsr' | 'hasScale' | 'hasStormAudioNav'>
    > = {};
    (Object.keys(PeripheralFieldByHeader) as (keyof typeof PeripheralFieldByHeader)[]).forEach(
      (header) => {
        const value = cell(header).toLowerCase();
        if (value === 'true') {
          peripheralFlags[PeripheralFieldByHeader[header]] = true;
        } else if (value !== 'false' && value !== '') {
          errorMessages.push(
            `Row ${rowNumber}: ${PeripheralLabelByHeader[header]} must be TRUE, FALSE, or blank, ` +
              `not "${cell(header)}".`,
          );
        }
      },
    );

    if (
      deviceType === undefined ||
      printerType === undefined ||
      paymentTerminalModel === undefined
    ) {
      return;
    }

    const name = cell('name');
    const printerMacAddress = cell('printer mac address');
    const paymentTerminalSerialNumber = cell('payment terminal identifier');
    const paymentTerminalIpAddress = cell('payment terminal ip address');
    const notes = cell('notes');
    kioskRows.push({
      serialNumber,
      locationId,
      ...(name && { name }),
      deviceType,
      printerType,
      ...(printerMacAddress && { printerMacAddress }),
      paymentTerminalModel,
      ...(paymentTerminalSerialNumber && { paymentTerminalSerialNumber }),
      ...(paymentTerminalIpAddress && { paymentTerminalIpAddress }),
      ...peripheralFlags,
      ...(notes && { notes }),
    });
  });

  if (!kioskRows.length && !errorMessages.length) {
    errorMessages.push('The CSV file does not contain any kiosk rows.');
  }

  return kioskRows;
}

export function parseKioskImportCsv(fileText: string): {
  errorMessages: string[];
  kioskRows: KioskImportRow[];
} {
  let csvRows: string[][];
  try {
    csvRows = CsvHelper.parseCsv(fileText);
  } catch (err) {
    return {
      errorMessages: [err instanceof Error ? err.message : 'The CSV file could not be parsed.'],
      kioskRows: [],
    };
  }

  const [headerCells, ...dataRows] = csvRows;
  if (!headerCells) {
    return { errorMessages: ['The CSV file is empty.'], kioskRows: [] };
  }

  const errorMessages: string[] = [];
  const kioskRows = parseRows(headerCells, dataRows, errorMessages);
  return { errorMessages, kioskRows: errorMessages.length ? [] : kioskRows };
}
